import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Wand2, Video, Image as ImageIcon, Megaphone, Code2, Sparkles, Play,
  CheckCircle2, Loader2, Circle, Cpu, Layers, Zap, Download,
} from "lucide-react";
import { AccessGate } from "@/components/AccessGate";
import api, { mediaUrl } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { VideoTools } from "@/components/VideoTools";
import { UploadFootage } from "@/components/UploadFootage";

const MODES = [
  { id: "app-builder", label: "App Builder", icon: Code2, hint: "Full-stack blueprint" },
  { id: "video", label: "Video Cinema", icon: Video, hint: "Cinematic synthesis" },
  { id: "image", label: "Hyper Image", icon: ImageIcon, hint: "Ultra-res assets" },
  { id: "ad", label: "Ad Campaign", icon: Megaphone, hint: "Marketing engine" },
];

const TEXT_ENGINES = [
  { id: "auto", label: "Auto (Concierge) · Gemini 3.1 Pro", cost: "₹0.90", speed: "balanced" },
  { id: "gpt-5.4", label: "ChatGPT · GPT-5.4", cost: "₹1.20", speed: "best quality" },
  { id: "gpt-5.4-mini", label: "ChatGPT · GPT-5.4 Mini", cost: "₹0.25", speed: "fastest" },
  { id: "gemini-3.1-pro", label: "Gemini 3.1 Pro", cost: "₹0.90", speed: "balanced" },
];
const MEDIA_ENGINE = { video: "fal.ai LTX/Wan (5–10s) or Cinematic Assembly (15s–10min)", image: "Nano Banana (Gemini)" };

export default function Studio() {
  const navigate = useNavigate();
  const { t } = useT();
  const [mode, setMode] = useState("image");
  const [prompt, setPrompt] = useState("");
  const [engine, setEngine] = useState(TEXT_ENGINES[0].id);
  const engineMeta = TEXT_ENGINES.find((e) => e.id === engine) || TEXT_ENGINES[0];
  const engineLabel = engineMeta.label;
  const [duration, setDuration] = useState("5s");
  const [videoMode, setVideoMode] = useState("standard");
  const DUR_SEC = { "5s": 5, "10s": 10, "15s": 15, "30s": 30, "1m": 60, "3m": 180, "5m": 300, "10m": 600 };
  const creditCost = (Math.ceil((DUR_SEC[duration] || 5) / 5) * 5 / 60) * (videoMode === "cinematic" ? 3 : 1);
  const [aspect, setAspect] = useState("16:9");
  const [steps, setSteps] = useState([]);
  const [pipeModel, setPipeModel] = useState(null);
  const [uploaded, setUploaded] = useState(null);
  const [activeStep, setActiveStep] = useState(-1);
  const [running, setRunning] = useState(false);
  const [output, setOutput] = useState(null);
  const pollRef = useRef(null);

  const pollVideo = (taskId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/studio/video/${taskId}`);
        if (r.data.status === "completed") {
          clearInterval(pollRef.current);
          setOutput({ type: "video", data: r.data });
          toast.success("Cinematic video ready");
        } else if (r.data.status === "failed") {
          clearInterval(pollRef.current);
          setOutput({ type: "video", data: r.data });
          toast.error(r.data.error || "Video render failed");
        } else if (r.data.status === "cancelled") {
          clearInterval(pollRef.current);
          setOutput({ type: "video", data: r.data });
          toast.message("Render cancelled", { description: r.data.error || "" });
        } else {
          setOutput({ type: "video", data: r.data });
        }
      } catch (e) {}
    }, 5000);
  };

  const cancelVideo = async () => {
    const tid = output?.data?.task_id;
    if (!tid) return;
    clearInterval(pollRef.current);
    try {
      await api.post(`/studio/video/${tid}/cancel`);
      setOutput({ type: "video", data: { status: "cancelled", error: "Cancelled — no further credits used." } });
      toast.message("Render cancelled", { description: "The queued job was cancelled to save credits." });
    } catch (e) { toast.error("Could not cancel"); }
  };

  const runPipeline = async (over = {}) => {
    const p = over.prompt ?? prompt, m = over.mode ?? mode, en = over.engine ?? engine;
    if (!p.trim()) { toast.error("Enter a prompt to orchestrate"); return; }
    clearInterval(pollRef.current);
    setRunning(true);
    setOutput(null);
    setSteps([]);
    setActiveStep(-1);

    try {
      const pipe = await api.post("/studio/pipeline", { prompt: p, mode: m, engine: en });
      const s = pipe.data.steps || [];
      setSteps(s);
      setPipeModel(pipe.data.model || null);
      toast.success(`Pipeline "${pipe.data.title}" generated`);

      for (let i = 0; i < s.length; i++) {
        setActiveStep(i);
        await new Promise((r) => setTimeout(r, 650));
      }
      setActiveStep(s.length);

      if (m === "video") {
        const res = await api.post("/studio/generate/video", { prompt: p, mode: m, duration, aspect_ratio: aspect, video_mode: videoMode });
        setOutput({ type: "video", data: { status: "rendering", ...res.data } });
        toast.message(`Rendering ${videoMode} video…`, { description: `${creditCost.toFixed(2)} video credits reserved · refunded automatically if the render fails.` });
        pollVideo(res.data.task_id);
        return;
      }

      const endpoint = {
        "image": "/studio/generate/image",
        "app-builder": "/studio/generate/app",
        "ad": "/studio/generate/ad",
      }[m];
      const res = await api.post(endpoint, { prompt: p, mode: m, engine: en });
      setOutput({ type: m, data: res.data });
      toast.success("Generation complete");
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error(e.response.data.detail, { action: { label: "Get a pack", onClick: () => navigate("/billing") } });
      } else {
        toast.error(e?.response?.data?.detail || "Generation failed");
      }
    } finally {
      setRunning(false);
    }
  };


  return (
    <div className="p-5 sm:p-8 max-w-[1400px] mx-auto">
      <AccessGate />
      <div className="mb-8 float-in flex flex-wrap items-start gap-4">
        <div className="flex-1 min-w-[280px]">
          <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-purple-400 mb-2">
            <Wand2 size={14} /> Unified Studio · Concierge + Creator merged
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
            One prompt. <span className="text-gradient">Every engine.</span>
          </h1>
          <p className="text-slate-400 mt-2 text-sm sm:text-base max-w-2xl">
            Describe what you want. The Concierge orchestrates a multi-step pipeline across the arsenal
            and assembles a finished asset.
          </p>
        </div>

      </div>


      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Prompt engine */}
        <div className="lg:col-span-2 space-y-4">
          <div data-testid="universal-prompt-input-container" className="glass rounded-2xl p-5 grain">
            <div className="grid grid-cols-2 gap-2 mb-4">
              {MODES.map((m) => (
                <button
                  key={m.id}
                  data-testid={`mode-${m.id}`}
                  onClick={() => setMode(m.id)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm border transition-colors ${
                    mode === m.id
                      ? "bg-gradient-to-r from-purple-600/25 to-cyan-500/15 border-purple-500/40 text-white"
                      : "border-white/10 text-slate-400 hover:text-white hover:bg-white/5"
                  }`}
                >
                  <m.icon size={16} />
                  <span className="truncate">{m.label}</span>
                </button>
              ))}
            </div>

            <textarea
              data-testid="studio-prompt-textarea"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={5}
              placeholder={
                mode === "app-builder"
                  ? "e.g. A SaaS dashboard for QuantX AI with pricing, auth and analytics..."
                  : mode === "video"
                  ? "e.g. A 9:16 cinematic Hindi product ad for a luxury watch, neon city..."
                  : mode === "ad"
                  ? "e.g. A conversion-focused campaign for an eco water bottle, Gen-Z..."
                  : "e.g. A cinematic portrait of a cyberpunk samurai in neon rain, 4k..."
              }
              className="w-full bg-[#0A0C14]/60 border border-white/10 rounded-xl p-3.5 text-sm outline-none focus:border-purple-500/40 transition-colors resize-none placeholder:text-slate-600"
            />

            <div className="mt-3 flex items-center gap-2">
              <span className="text-[10px] font-mono text-slate-600">~{Math.max(1, Math.ceil(prompt.length / 4))} tokens</span>
              <div className="flex-1" />
              <div data-testid="engine-current-label" className="text-[10px] font-mono text-slate-600 flex items-center gap-1">
                <Cpu size={11} /> {engineLabel}
              </div>
            </div>

            <div className="mt-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">{t("Text engine")}</div>
              <select
                data-testid="engine-selector"
                value={engine}
                onChange={(e) => setEngine(e.target.value)}
                className="w-full bg-[#0A0C14]/60 border border-white/10 rounded-xl p-2.5 text-sm outline-none focus:border-purple-500/40"
              >
                {TEXT_ENGINES.map((en) => (
                  <option key={en.id} value={en.id} className="bg-[#0E1019]">{en.label} — ~{en.cost}/run · {en.speed}</option>
                ))}
              </select>
              <div data-testid="engine-cost-tags" className="mt-2 flex flex-wrap gap-1.5">
                {TEXT_ENGINES.filter((e) => e.id !== "auto").map((en) => (
                  <button key={en.id} type="button" data-testid={`engine-tag-${en.id}`} onClick={() => setEngine(en.id)}
                    className={`text-[10px] font-mono px-2 py-1 rounded-md border transition-colors ${engine === en.id ? "border-purple-500/50 bg-purple-500/15 text-purple-100" : "border-white/10 text-slate-500 hover:text-white hover:bg-white/5"}`}>
                    {en.label.replace("ChatGPT · ", "")} <span className={engine === en.id ? "text-emerald-300" : "text-emerald-400/70"}>~{en.cost}</span> <span className="text-slate-600">· {en.speed}</span>
                  </button>
                ))}
              </div>
              <div data-testid="engine-hint" className="text-[10px] text-slate-600 mt-1.5">
                <span data-testid="engine-cost-current" className="text-emerald-400/80">~{engineMeta.cost} est. per run</span> ·{" "}
                {MEDIA_ENGINE[mode]
                  ? t("Plans the pipeline; media renders on") + ` ${MEDIA_ENGINE[mode]}`
                  : t("Plans the pipeline and writes the result")}
              </div>
            </div>

            {mode === "video" && (
              <div className="mt-3 rounded-xl border border-white/10 bg-[#0A0C14]/40 p-3 space-y-3">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">{t("Video length")}</div>
                  <div data-testid="video-duration" className="flex flex-wrap gap-1.5">
                    {[["5s", "5s"], ["10s", "10s"], ["15s", "15s"], ["30s", "30s"], ["1m", "1 min"], ["3m", "3 min"], ["5m", "5 min"], ["10m", "10 min"]].map(([v, l]) => (
                      <button key={v} type="button" data-testid={`video-duration-${v}`} onClick={() => setDuration(v)}
                        className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${duration === v ? "border-purple-500/50 bg-purple-500/15 text-purple-100" : "border-white/10 text-slate-400 hover:text-white hover:bg-white/5"}`}>{l}</button>
                    ))}
                  </div>
                  <div className="text-[10px] text-slate-600 mt-1.5">5s & 10s: generative AI video clip · 15s–10 min: full-length Cinematic Assembly — scripted scenes, AI visuals, narration & captions rendered to one video</div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">{t("Video quality")}</div>
                  <div data-testid="video-mode" className="flex gap-1.5">
                    {[["standard", "Standard · 1 credit/min"], ["cinematic", "Cinematic · 3 credits/min"]].map(([v, l]) => (
                      <button key={v} type="button" data-testid={`video-mode-${v}`} onClick={() => setVideoMode(v)}
                        className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${videoMode === v ? "border-amber-500/50 bg-amber-500/15 text-amber-100" : "border-white/10 text-slate-400 hover:text-white hover:bg-white/5"}`}>{l}</button>
                    ))}
                  </div>
                  <div data-testid="video-credit-cost" className="text-[11px] font-mono text-amber-300 mt-1.5">Cost: {creditCost.toFixed(2)} video credit{creditCost === 1 ? "" : "s"} · reserved on submit, refunded if the render fails</div>
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1.5">{t("Aspect ratio")}</div>
                  <div data-testid="video-aspect" className="flex gap-1.5">
                    {[["16:9", "16:9 Landscape"], ["9:16", "9:16 Vertical"], ["1:1", "1:1 Square"]].map(([v, l]) => (
                      <button key={v} type="button" data-testid={`video-aspect-${v.replace(":", "x")}`} onClick={() => setAspect(v)}
                        className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${aspect === v ? "border-cyan-500/50 bg-cyan-500/15 text-cyan-100" : "border-white/10 text-slate-400 hover:text-white hover:bg-white/5"}`}>{l}</button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <button
              data-testid="generate-pipeline-button"
              onClick={runPipeline}
              disabled={running}
              className="mt-4 w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-gradient-to-r from-purple-600 via-indigo-600 to-cyan-600 text-white font-semibold pulse-glow hover:scale-[1.01] transition-transform disabled:opacity-60 disabled:hover:scale-100"
            >
              {running ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
              {t(running ? "Generating..." : mode === "video" ? "Generate Video" : mode === "image" ? "Generate Image" : mode === "ad" ? "Generate Ad Campaign" : mode === "app-builder" ? "Generate App Blueprint" : "Generate")}
            </button>
          </div>

          <UploadFootage onUploaded={(d) => {
            clearInterval(pollRef.current);
            setUploaded(d);
            setSteps([]);
            setOutput({ type: "video", data: { status: "completed", url: d.url, media_id: d.media_id, engine: "Your footage", uploaded: true, duration: d.duration, width: d.width, height: d.height, filename: d.filename } });
          }} />
          {uploaded && <div data-testid="uploaded-footage-note" className="text-[11px] text-slate-500 px-1">Working on <span className="text-slate-300">{uploaded.filename}</span> — use the tools in Output Studio to trim, reframe, add music, auto-caption or write platform copy.</div>}

          {/* Pipeline visualizer */}
          <AnimatePresence>
            {steps.length > 0 && (
              <motion.div
                data-testid="generated-pipeline-visualizer"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass rounded-2xl p-5"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Layers size={15} className="text-cyan-400" />
                  <span className="font-display font-semibold text-sm">Generated Pipeline</span>
                  {pipeModel && <span data-testid="pipeline-model" className="ml-auto text-[10px] font-mono px-2 py-0.5 rounded-md bg-purple-500/15 text-purple-300 border border-purple-500/20">{pipeModel}</span>}
                </div>
                <div className="space-y-2.5">
                  {steps.map((s, i) => {
                    const done = i < activeStep;
                    const active = i === activeStep;
                    return (
                      <div
                        key={s.id || i}
                        data-testid={`pipeline-step-${i}`}
                        className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${
                          active ? "border-cyan-500/40 bg-cyan-500/5" : done ? "border-emerald-500/30 bg-emerald-500/5" : "border-white/10"
                        }`}
                      >
                        <div className="mt-0.5">
                          {done ? (
                            <CheckCircle2 size={17} className="text-emerald-400" />
                          ) : active ? (
                            <Loader2 size={17} className="text-cyan-400 animate-spin" />
                          ) : (
                            <Circle size={17} className="text-slate-600" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium">{s.name}</div>
                          <div className="text-xs text-slate-500">{s.detail}</div>
                          <div className="mt-1 flex items-center gap-2 text-[10px] font-mono text-slate-600">
                            <span className="text-purple-400">{s.engine}</span>
                            <span>·</span>
                            <span>{s.eta}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Output panel */}
        <div className="lg:col-span-3">
          <div data-testid="studio-output-tabs-panel" className="glass rounded-2xl p-5 min-h-[520px] grain relative overflow-hidden">
            <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-5">
                <Zap size={15} className="text-amber-400" />
                <span className="font-display font-semibold text-sm">Output Studio</span>
                <span className="text-[10px] font-mono uppercase text-slate-600 ml-auto">{mode}</span>
              </div>

              {!output && !running && (
                <div className="h-[420px] flex flex-col items-center justify-center text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-600/30 to-cyan-500/20 flex items-center justify-center mb-4">
                    <Wand2 size={28} className="text-purple-300" />
                  </div>
                  <p className="text-slate-500 text-sm max-w-xs">
                    Your finished asset will assemble here once the pipeline completes.
                  </p>
                </div>
              )}

              {running && !output && (
                <div className="space-y-3">
                  <div className="h-64 rounded-xl shimmer" />
                  <div className="h-4 w-2/3 rounded shimmer" />
                  <div className="h-4 w-1/2 rounded shimmer" />
                </div>
              )}

              {output && <OutputView output={output} onCancel={cancelVideo} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function OutputView({ output, onCancel }) {
  const { type, data } = output;

  if (type === "image") {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
        <img
          data-testid="output-image"
          src={mediaUrl(data.url)}
          alt="generated"
          className="w-full rounded-xl border border-white/10"
        />
        <p className="text-sm text-slate-400">{data.caption}</p>
        <a
          href={mediaUrl(data.url)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 text-xs font-mono text-cyan-400 hover:text-cyan-300"
        >
          <Download size={14} /> Open full resolution
        </a>
      </motion.div>
    );
  }

  if (type === "video") {
    if (data.status === "rendering") {
      return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
          <div className="relative rounded-xl overflow-hidden border border-white/10 bg-black h-72 flex flex-col items-center justify-center">
            <Loader2 size={34} className="text-cyan-400 animate-spin mb-3" />
            <div className="font-display font-semibold text-sm">Rendering cinematic video</div>
            <div className="text-xs text-slate-500 mt-1">{data.engine}{data.phase ? ` · ${data.phase}` : ""} · typically 1–3 minutes</div>
            <div className="mt-4 w-48 h-1 rounded-full overflow-hidden bg-white/10">
              <div className="h-full w-1/2 bg-gradient-to-r from-purple-500 to-cyan-500 shimmer" />
            </div>
          </div>
          <div className="text-[10px] font-mono text-slate-600">engine: {data.engine} · {data.aspect_ratio} · {data.duration}</div>
          <button data-testid="cancel-video-button" onClick={onCancel}
            className="text-xs px-4 py-2 rounded-full border border-white/15 text-slate-300 hover:border-red-500/40 hover:text-red-300 transition-colors">
            Cancel render (save credits)
          </button>
        </motion.div>
      );
    }
    if (data.status === "cancelled") {
      return (
        <div className="h-72 flex flex-col items-center justify-center text-center">
          <div className="text-amber-400 font-semibold">Render cancelled</div>
          <div className="text-xs text-slate-500 mt-1 max-w-xs">{data.error}</div>
        </div>
      );
    }
    if (data.status === "failed") {
      return (
        <div className="h-72 flex flex-col items-center justify-center text-center">
          <div className="text-red-400 font-semibold">Render failed</div>
          <div className="text-xs text-slate-500 mt-1 max-w-xs">{data.error}</div>
        </div>
      );
    }
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
        <div className="relative rounded-xl overflow-hidden border border-white/10 bg-black">
          <video
            data-testid="output-video"
            src={mediaUrl(data.url)}
            controls
            autoPlay
            loop
            muted
            playsInline
            className="w-full"
          />
          <div className="absolute top-3 left-3 text-[10px] font-mono px-2 py-1 rounded-md bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
            {data.uploaded ? "UPLOADED" : "REAL"} · {data.engine || "fal.ai"}
          </div>
        </div>
        {data.uploaded && (
          <p data-testid="uploaded-video-meta" className="text-xs text-slate-400">
            {data.filename} · {data.duration ? `${Number(data.duration).toFixed(1)}s` : ""} · {data.width}×{data.height} — ready for trim, reframe, music, Whisper captions and caption writer below
          </p>
        )}
        {data.extended && (
          <p data-testid="video-assembly-note" className="text-xs text-slate-400">
            Full-length Cinematic Assembly · {data.scenes} scenes · narration {data.narration_media_id ? "included" : "unavailable"} · captions burned in
          </p>
        )}
        <a href={mediaUrl(data.url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-xs font-mono text-cyan-400 hover:text-cyan-300">
          <Download size={14} /> Download MP4
        </a>
        {data.media_id && <VideoTools mediaId={data.media_id} />}
      </motion.div>
    );
  }

  if (type === "app-builder") {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
        <div>
          <h3 className="font-display text-xl font-bold">{data.app_name}</h3>
          <p className="text-sm text-slate-400 mt-1">{data.description}</p>
          {data.model && <div data-testid="output-model" className="text-[10px] font-mono text-slate-500 mt-1">written by {data.model}</div>}
          <div className="flex flex-wrap gap-2 mt-3">
            {(data.tech_stack || []).map((t) => (
              <span key={t} className="text-[10px] font-mono px-2 py-1 rounded-md bg-cyan-500/10 text-cyan-300 border border-cyan-500/20">{t}</span>
            ))}
          </div>
        </div>
        <Section title="Pages">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {(data.pages || []).map((p, i) => (
              <div key={i} className="p-3 rounded-xl border border-white/10 bg-white/5">
                <div className="text-sm font-medium">{p.name}</div>
                <div className="text-xs text-slate-500">{p.purpose}</div>
              </div>
            ))}
          </div>
        </Section>
        <Section title="Data Models">
          <div className="space-y-2">
            {(data.data_models || []).map((m, i) => (
              <div key={i} className="p-3 rounded-xl border border-white/10 bg-white/5 font-mono text-xs">
                <div className="text-purple-300 font-semibold">{m.name}</div>
                <div className="text-slate-400 mt-1">{(m.fields || []).join(" · ")}</div>
                {m.relations && <div className="text-slate-600 mt-1">→ {m.relations}</div>}
              </div>
            ))}
          </div>
        </Section>
        <Section title="Roles (RBAC)">
          <div className="flex flex-wrap gap-2">
            {(data.roles || []).map((r, i) => (
              <div key={i} className="px-3 py-2 rounded-xl border border-white/10 bg-white/5">
                <span className="text-sm font-semibold text-amber-300">{r.name}</span>
                <span className="text-xs text-slate-500 block">{r.permissions}</span>
              </div>
            ))}
          </div>
        </Section>
      </motion.div>
    );
  }

  if (type === "ad") {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
        <div>
          <h3 className="font-display text-xl font-bold">{data.campaign_name}</h3>
          <p className="text-xs text-slate-500 mt-1">Target: {data.target_audience}</p>
          {data.model && <div data-testid="output-model" className="text-[10px] font-mono text-slate-500 mt-1">written by {data.model}</div>}
        </div>
        <div className="space-y-3">
          {(data.variations || []).map((v, i) => (
            <div key={i} className="p-4 rounded-xl border border-white/10 bg-white/5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-mono px-2 py-1 rounded-md bg-purple-500/15 text-purple-300">{v.platform}</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-mono text-slate-500">CVR</span>
                  <span className="text-sm font-bold text-emerald-400">{v.conversion_score}</span>
                </div>
              </div>
              <div className="text-sm font-semibold">{v.headline}</div>
              <div className="text-xs text-slate-400 mt-1">{v.body}</div>
              <div className="mt-2 inline-block text-xs font-medium text-cyan-300 border border-cyan-500/30 rounded-full px-3 py-1">{v.cta}</div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(data.hashtags || []).map((h) => (
            <span key={h} className="text-[10px] font-mono text-slate-500">{h}</span>
          ))}
        </div>
      </motion.div>
    );
  }
  return null;
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">{title}</div>
      {children}
    </div>
  );
}
